import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const tourOptions: { title: string; price: number }[] = [
  { title: "One Day Tour to Angkor Wat, Bayon & Ta Prohm", price: 95 },
  { title: "One Day Banteay Srei & Grand Circuit Heritage Tour", price: 110 },
  { title: "Phnom Kulen Waterfall, Reclining Buddha & River of 1000 Lingas (1 Day)", price: 120 },
  { title: "One Day Kampong Kleang Floating Village & Local Life Tour", price: 95 },
  { title: "2-Day Siem Reap Tour: Angkor Temples & Kampong Phluk", price: 240 },
  { title: "Three Day Siem Reap Temple & Local Experience Tour", price: 380 },
  { title: "7 Days Tours Across Cambodia", price: 980 },
  { title: "10 Days Tours Across Cambodia", price: 1450 },
  { title: "18 Days Tours Across Cambodia", price: 2480 },
  { title: "21 Days Tours Across Cambodia", price: 2890 },
  { title: "25 Days Tours Across Cambodia", price: 3380 },
  { title: "30-Day Ultimate Cambodia Adventure", price: 3980 },
];

const formatPrice = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [tour, setTour] = useState("");
  const [guests, setGuests] = useState(1);
  const [requests, setRequests] = useState("");
  const [prefillRate, setPrefillRate] = useState<number | null>(null);
  const [extraTours, setExtraTours] = useState<{ title: string; price: number }[]>([]);

  const allTours = useMemo(() => {
    const merged = [...extraTours, ...tourOptions];
    return merged.filter(
      (t, i, arr) => arr.findIndex((x) => x.title === t.title) === i
    );
  }, [extraTours]);

  const selectedTour = useMemo(
    () => allTours.find((t) => t.title === tour),
    [tour, allTours]
  );
  const rate = prefillRate ?? selectedTour?.price ?? 0;
  const total = rate * guests;

  useEffect(() => {
    const apply = () => {
      const raw = sessionStorage.getItem("bookingPrefill");
      if (!raw) return;
      try {
        const data = JSON.parse(raw) as {
          tour?: string;
          date?: string;
          guests?: number;
          rate?: number;
        };
        if (data.tour) {
          setTour(data.tour);
          if (!tourOptions.some((t) => t.title === data.tour)) {
            setExtraTours([{ title: data.tour!, price: data.rate ?? 0 }]);
          }
        }
        if (data.date) setDate(data.date);
        if (typeof data.guests === "number") setGuests(data.guests);
        if (typeof data.rate === "number") setPrefillRate(data.rate);
      } catch {
        /* ignore */
      }
      sessionStorage.removeItem("bookingPrefill");
    };
    apply();
    window.addEventListener("bookingPrefillUpdated", apply);
    return () => window.removeEventListener("bookingPrefillUpdated", apply);
  }, []);

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    const bookingId = (typeof crypto !== "undefined" && "randomUUID" in crypto)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const templateData = {
      name,
      email,
      tour: tour || "—",
      date: date || "—",
      guests,
      rate: rate ? `${formatPrice(rate)} / pax` : "—",
      total: rate ? formatPrice(total) : "—",
      requests: requests || "",
    };

    try {
      const [clientRes, teamRes] = await Promise.all([
        supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "booking-client-confirmation",
            recipientEmail: email,
            idempotencyKey: `booking-client-${bookingId}`,
            templateData,
          },
        }),
        supabase.functions.invoke("send-transactional-email", {
          body: {
            templateName: "booking-team-notification",
            idempotencyKey: `booking-team-${bookingId}`,
            templateData,
          },
        }),
      ]);

      if (clientRes.error || teamRes.error) {
        console.error("Email error", clientRes.error, teamRes.error);
        toast.error("We had trouble sending your request", {
          description: "Please try again, or email us directly at ladongnan@gmail.com.",
          duration: 8000,
        });
      } else {
        toast.success("Booking Request Sent!", {
          description: "We've emailed your confirmation and notified our team. We'll reply shortly. Thank you!",
          duration: 8000,
        });
        setName(""); setEmail(""); setDate(""); setTour(""); setGuests(1); setRequests("");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong", {
        description: "Please try again or contact ladongnan@gmail.com directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const hasAny = name || email || date || tour || requests || guests > 1;

  return (
    <section id="contact" className="relative bg-jungle text-background py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0 grain opacity-100" />
      <div className="container relative grid md:grid-cols-2 gap-16 items-start">
        <div className="md:sticky md:top-28">
          <p className="text-xs uppercase tracking-[0.4em] text-gold mb-6">— Contact & Booking</p>
          <h2 className="font-display text-5xl leading-[1] text-balance mb-8 md:text-3xl">
            The temples have waited <em className="font-light">nine</em> centuries. A few weeks more is fine.
          </h2>
          <p className="text-background/75 font-light leading-relaxed max-w-md">
            Tell us when you'd like to travel, which sojourn calls to you, and any special requests. We'll reply within 24 hours with a hand-drafted itinerary.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-background/5 backdrop-blur p-8 md:p-10 border border-background/10">
          {hasAny && (
            <div className="border border-gold/30 bg-background/5 p-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">— Booking Summary</p>
              <ul className="space-y-2 text-sm font-light">
                <li className="flex justify-between gap-3">
                  <span className="text-background/80">Client</span>
                  <span className="text-right">{name || "—"}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-background/80">Email</span>
                  <span className="text-right break-all">{email || "—"}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-background/80">Preferred Date</span>
                  <span className="text-right">{date || "—"}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-background/80">Tour</span>
                  <span className="text-right">{tour || "—"}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-background/80">Guests</span>
                  <span className="text-right tabular-nums">{guests}</span>
                </li>
                <li className="flex justify-between gap-3">
                  <span className="text-background/80">Standard Rate</span>
                  <span className="text-right tabular-nums">{rate ? `${formatPrice(rate)} / pax` : "—"}</span>
                </li>
                {requests && (
                  <li className="flex justify-between gap-3">
                    <span className="text-background/80">Special Requests</span>
                    <span className="text-right whitespace-pre-wrap">{requests}</span>
                  </li>
                )}
              </ul>
              <div className="mt-4 border-t border-background/15 pt-3 flex items-baseline justify-between">
                <span className="text-[10px] uppercase tracking-[0.3em] text-background/80">Estimated Total</span>
                <span className="font-display text-2xl tabular-nums text-gold">
                  {rate ? formatPrice(total) : "—"}
                </span>
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-background/85 block mb-2">Name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full bg-transparent border-b border-background/30 py-3 text-background placeholder:text-background/85 focus:outline-none focus:border-gold transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-background/85 block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@somewhere.com"
                className="w-full bg-transparent border-b border-background/30 py-3 text-background placeholder:text-background/85 focus:outline-none focus:border-gold transition-colors"
              />
            </div>
          </div>

          <div>
            <label htmlFor="contact-tour-select" className="text-[10px] uppercase tracking-[0.3em] text-background/85 block mb-2">Select Tour *</label>
            <select
              id="contact-tour-select"
              aria-label="Select tour"
              required
              value={tour}
              onChange={(e) => {
                setTour(e.target.value);
                setPrefillRate(null);
              }}
              className="w-full bg-transparent border-b border-background/30 py-3 text-background focus:outline-none focus:border-gold transition-colors"
            >
              <option value="" className="text-ink">Choose a sojourn…</option>
              {allTours.map((t) => (
                <option key={t.title} value={t.title} className="text-ink">
                  {t.title} — {formatPrice(t.price)}/pax
                </option>
              ))}
            </select>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-background/85 block mb-2">Preferred Date *</label>
              <input
                type="date"
                required
                value={date}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setDate(e.target.value)}
                className="w-full bg-transparent border-b border-background/30 py-3 text-background focus:outline-none focus:border-gold transition-colors [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-background/85 block mb-2">Number of Guests</label>
              <div className="flex items-center justify-between border-b border-background/30 py-2">
                <button
                  type="button"
                  onClick={() => setGuests((g) => Math.max(1, g - 1))}
                  aria-label="Decrease guests"
                  className="h-11 w-11 rounded-full border border-background/30 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
                >−</button>
                <span className="font-display text-lg tabular-nums">{guests}</span>
                <button
                  type="button"
                  onClick={() => setGuests((g) => Math.min(20, g + 1))}
                  aria-label="Increase guests"
                  className="h-11 w-11 rounded-full border border-background/30 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
                >+</button>
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-background/85 block mb-2">Special Requests (Optional)</label>
            <textarea
              rows={3}
              value={requests}
              onChange={(e) => setRequests(e.target.value)}
              placeholder="Dietary needs, accessibility, sunrise at Bayon…"
              className="w-full bg-transparent border-b border-background/30 py-3 text-background placeholder:text-background/85 focus:outline-none focus:border-gold transition-colors resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gold text-ink py-4 text-xs uppercase tracking-[0.3em] hover:bg-background transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending…" : "Send Booking Request"}
          </button>
        </form>
      </div>
    </section>
  );
};
export default Contact;
